import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  dataTableHeadClass,
  DataTablePanel,
  PageShell,
} from '../../components/DataTablePanel';
import {
  Callout,
  Card,
  ListEmptyState,
  LoadingSpinner,
} from '../../components/ui';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import { AgingBucket, CompanyExpenseAgingReport } from '../../types/reports';
import { formatDate, formatMoney } from '../../utils/format';
import { ReportExportButtons } from './ReportExportButtons';
import { exportCompanyExpenseAging } from './exportCompanyExpenseReports';

const BUCKETS: AgingBucket[] = [
  'notDue',
  'd1_30',
  'd31_60',
  'd61_90',
  'd90plus',
];

export function CompanyExpenseAgingPage() {
  const labels = t().reports;
  const [report, setReport] = useState<CompanyExpenseAgingReport | null>(null);
  const [activeBucket, setActiveBucket] = useState<AgingBucket>('d1_30');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await api.getCompanyExpenseAgingReport();
        if (!cancelled) setReport(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const items = report?.buckets[activeBucket].items ?? [];

  return (
    <PageShell>
      <div className="flex shrink-0 items-center justify-between gap-3 px-1">
        <div className="text-sm text-ink-muted">
          {labels.asOf}: {report ? formatDate(report.asOf) : '—'}
        </div>
        {report ? (
          <ReportExportButtons
            disabled={!report}
            onExcel={() =>
              exportCompanyExpenseAging({ report, format: 'excel' })
            }
            onPdf={() => exportCompanyExpenseAging({ report, format: 'pdf' })}
          />
        ) : null}
      </div>

      {error ? (
        <Callout variant="danger" className="shrink-0">
          {error}
        </Callout>
      ) : null}

      {loading ? (
        <LoadingSpinner message={t().common.loading} />
      ) : !report || report.totals.count === 0 ? (
        <ListEmptyState title={t().common.empty} />
      ) : (
        <>
          <div className="grid shrink-0 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            {BUCKETS.map((bucket) => {
              const b = report.buckets[bucket];
              const active = bucket === activeBucket;
              return (
                <button
                  key={bucket}
                  type="button"
                  onClick={() => setActiveBucket(bucket)}
                  className={[
                    'rounded-lg border p-3 text-left text-sm transition-colors',
                    active
                      ? 'border-brand bg-brand-soft'
                      : 'border-line bg-surface hover:bg-surface-muted/40',
                  ].join(' ')}
                >
                  <div className="text-xs text-ink-faint">
                    {labels.buckets[bucket]}
                  </div>
                  <div className="mt-2 text-lg font-semibold tabular-nums">
                    {formatMoney(b.gross)}
                  </div>
                  <div className="mt-1 text-xs text-ink-muted">
                    {b.count} · {formatMoney(b.net)} / {formatMoney(b.vat)}
                  </div>
                </button>
              );
            })}
            <Card className="border-brand/30 bg-brand-soft p-3 text-sm">
              <div className="text-xs text-brand">{t().common.total}</div>
              <div className="mt-2 text-lg font-semibold tabular-nums text-brand">
                {formatMoney(report.totals.gross)}
              </div>
            </Card>
          </div>

          <DataTablePanel>
            {items.length === 0 ? (
              <ListEmptyState title={t().common.empty} />
            ) : (
              <table className="min-w-full text-sm">
                <thead className={dataTableHeadClass}>
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">
                      {t().companyExpenses.dueDate}
                    </th>
                    <th className="px-3 py-2 text-left font-medium">
                      {labels.companyExpenseVendor}
                    </th>
                    <th className="px-3 py-2 text-left font-medium">
                      {t().companyExpenses.category}
                    </th>
                    <th className="px-3 py-2 text-left font-medium">
                      {labels.bucket}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {labels.companyExpenseDaysOverdue}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {labels.expenseNet}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {labels.expenseVat}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {labels.expenseGross}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr
                      key={`${item.expenseId}-${item.kind}-${index}`}
                      className="border-t border-line"
                    >
                      <td className="px-3 py-2 whitespace-nowrap">
                        <Link
                          to={`/company-expenses/${item.expenseId}/edit`}
                          className="text-brand hover:underline"
                        >
                          {formatDate(item.dueDate)}
                        </Link>
                      </td>
                      <td className="px-3 py-2">
                        {item.vendorName || '—'}
                        {item.documentNo ? (
                          <div className="text-xs text-ink-faint">
                            {item.documentNo}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-3 py-2">{item.categoryName}</td>
                      <td className="px-3 py-2">
                        {item.kind === 'vat'
                          ? labels.companyExpenseKindVat
                          : item.kind === 'net'
                            ? labels.companyExpenseKindNet
                            : labels.companyExpenseKindBoth}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {item.daysOverdue}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatMoney(item.remainingNet)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatMoney(item.remainingVat)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatMoney(item.remainingGross)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </DataTablePanel>
        </>
      )}
    </PageShell>
  );
}
